import { Controller } from "@hotwired/stimulus"

const PRORATED_ACCOUNT_KEY = "pro-rated"

const SALES_TAX_REGEX = /\b(?:sales?\s+tax|tax)\b/i
const INCLUDED_TAX_REGEX = /\b(?:incl(?:uded|\.?)|including|includes?)\s*\.?\s*tax\b|\btax\s+incl(?:uded|\.?)?\b/i
const FUEL_SURCHARGE_REGEX = /\b(?:fuel(?:\s+surcharge|\s+charge)?|delivery\s+fuel)\b/i
const COPY_ICON_SVG = `
  <svg class="invoice-total-pill__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
`.trim()
const FILTER_ICON_SVG = `
  <svg class="invoice-total-pill__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
    <path d="M3 5h18l-7 8v5l-4 2v-7L3 5z"></path>
  </svg>
`.trim()
const COA_PALETTE_SUFFIXES = ["amber", "emerald", "cyan", "violet", "rose", "indigo"]

export default class extends Controller {
  static values = {
    taxExemptAccountNames: Array,
  }

  static targets = ["pills", "rowsContainer", "invoiceTotal", "totalsWarning", "totalsMessage"]

  connect() {
    this.activeFilterKey = null
    this.refresh()
  }

  refresh() {
    if (!this.hasPillsTarget || !this.hasRowsContainerTarget) return

    const rows = this.buildRollup()
    rows.sort((left, right) => this.compareRows(left, right))
    this.ensureActiveFilterExists(rows)
    this.renderPills(rows)
    this.applyRowFilter()
    this.updateTotalsWarning()
  }

  // Live total reconciliation: recompute the sum of all line-item amounts and compare it to the
  // invoice total on every edit. Shows a banner when they disagree; hides it once they tie out.
  updateTotalsWarning() {
    if (!this.hasTotalsWarningTarget) return

    const invoiceTotal = this.readInvoiceTotal()
    if (invoiceTotal === null) {
      this.hideTotalsWarning()
      this.announceReconciliation(null)
      return
    }

    const deltaCents = this.lineItemsSumCents() - this.toCents(invoiceTotal)
    if (deltaCents === 0) {
      this.hideTotalsWarning()
      this.announceReconciliation(0)
      return
    }

    if (this.hasTotalsMessageTarget) {
      const lineSum = this.formatCurrency(this.fromCents(this.lineItemsSumCents()))
      const total = this.formatCurrency(invoiceTotal)
      const off = this.formatCurrency(this.fromCents(Math.abs(deltaCents)))
      const direction = deltaCents > 0 ? "over" : "under"
      this.totalsMessageTarget.textContent =
        `Line items add up to ${lineSum}, which is ${off} ${direction} the invoice total of ${total}. ` +
        "Adjust the line amounts or the total so they match."
    }

    this.showTotalsWarning()
    this.announceReconciliation(deltaCents)
  }

  // The provenance controller flags rows whose amount it could not find in the PDF's amount
  // column, and on the measured corpus those are overwhelmingly the ordered-but-not-shipped
  // rows that make the line items overshoot. Its flags and this banner are therefore two
  // views of one finding, and they have to agree about whether there is currently anything
  // wrong. Publishing the delta keeps the dependency one-way: this controller still knows
  // nothing about provenance, and the banner reads the same with the listener absent.
  //
  // deltaCents is null when there is no invoice total to reconcile against, 0 when the
  // invoice ties out, and signed line-sum-minus-total otherwise.
  announceReconciliation(deltaCents) {
    this.dispatch("reconciliation", { detail: { deltaCents } })
  }

  // Stimulus connects this controller before the ones nested inside the form, so the
  // announcement above has already been made and missed by the time they start listening.
  // Without this a freshly loaded invoice would sit on a stale reconciliation state until
  // the reviewer's first keystroke - which is exactly the case that has to work untouched.
  republishReconciliation() {
    this.updateTotalsWarning()
  }

  lineItemsSumCents() {
    let cents = 0
    this.rowElements().forEach((row) => {
      const lineTotal = this.readLineTotal(row)
      if (lineTotal === null) return
      cents += this.toCents(lineTotal)
    })
    return cents
  }

  readInvoiceTotal() {
    const field = this.hasInvoiceTotalTarget
      ? this.invoiceTotalTarget
      : this.element.querySelector("input[name='invoice[invoice_total]']")
    return field ? this.parseAmount(field.value) : null
  }

  showTotalsWarning() {
    this.totalsWarningTarget.classList.remove("invoice-is-hidden")
  }

  hideTotalsWarning() {
    this.totalsWarningTarget.classList.add("invoice-is-hidden")
  }

  toggleFilter(event) {
    const trigger = event.currentTarget
    if (!trigger) return

    const key = this.normalizeKey(
      trigger.dataset.invoiceCoaTotalsKey ||
      trigger.closest(".invoice-total-pill")?.dataset.invoiceCoaTotalsKey,
    )
    if (!key) return

    this.activeFilterKey = this.activeFilterKey === key ? null : key
    this.applyRowFilter()
  }

  buildRollup() {
    const totalsCents = new Map()
    const basisCents = new Map()
    const subtotalCents = new Map()
    const accountNames = new Map()
    const taxSource = this.sourceBucket()
    const fuelSource = this.sourceBucket()

    this.rowElements().forEach((row) => {
      const lineTotal = this.readLineTotal(row)
      if (lineTotal === null) return

      const cents = this.toCents(lineTotal)
      const accountName = this.readAccountName(row)
      const accountKey = this.normalizeKey(accountName)
      const description = this.readDescription(row)

      if (accountKey) {
        accountNames.set(accountKey, this.displayAccountName(accountName))
        this.addCents(totalsCents, accountKey, cents)
      }

      if (this.isFuelSurchargeLine({ description, accountKey })) {
        this.addSourceAmount(fuelSource, accountKey, cents)
        return
      }

      if (this.isSalesTaxLine(description)) {
        this.addSourceAmount(taxSource, accountKey, cents)
        return
      }

      if (accountKey) {
        this.addCents(subtotalCents, accountKey, cents)
      }

      if (accountKey && cents > 0) {
        this.addCents(basisCents, accountKey, cents)
      }
    })

    let salesTaxTargets = this.basisTargetKeys(basisCents, accountNames, { includeTaxExempt: false })
    if (taxSource.totalCents !== 0 && salesTaxTargets.length === 0) {
      salesTaxTargets = this.basisTargetKeys(basisCents, accountNames, { includeTaxExempt: true })
    }

    this.allocateSource({
      totalsCents,
      accountNames,
      source: taxSource,
      targetKeys: salesTaxTargets,
      basisCents,
    })

    this.allocateSource({
      totalsCents,
      accountNames,
      source: fuelSource,
      targetKeys: this.basisTargetKeys(basisCents, accountNames, { includeTaxExempt: true }),
      basisCents,
    })

    const rows = []
    totalsCents.forEach((totalCents, key) => {
      if (totalCents === 0) return

      rows.push({
        key,
        name: accountNames.get(key) || this.displayAccountName(key),
        totalCents,
        subtotalCents: subtotalCents.get(key) || 0,
        subtotalLabel: this.subtotalLabelForAccountName(accountNames.get(key) || this.displayAccountName(key)),
      })
    })

    return rows
  }

  rowElements() {
    return Array.from(this.rowsContainerTarget.querySelectorAll(".invoice-line-item"))
  }

  readAccountName(row) {
    const form = row.querySelector("[data-invoice-row-edit-target='form']")
    const isEditing = form && !form.classList.contains("invoice-is-hidden")

    if (isEditing) {
      return this.cleanAccountName(this.fieldValue(form, "account_name"))
    }

    const display = row.querySelector(".invoice-line-item__value--account")
    return this.cleanAccountName(display ? display.textContent : "")
  }

  readDescription(row) {
    const form = row.querySelector("[data-invoice-row-edit-target='form']")
    const isEditing = form && !form.classList.contains("invoice-is-hidden")

    if (isEditing) {
      return this.fieldValue(form, "description")
    }

    const display = row.querySelector(".invoice-line-item__description-text")
    return String(display ? display.textContent : "").trim()
  }

  readLineTotal(row) {
    const form = row.querySelector("[data-invoice-row-edit-target='form']")
    const isEditing = form && !form.classList.contains("invoice-is-hidden")

    if (isEditing) {
      return this.parseAmount(this.fieldValue(form, "line_total"))
    }

    const display = row.querySelector(".invoice-line-item__value--amount")
    return this.parseAmount(display ? display.textContent : "")
  }

  cleanAccountName(value) {
    const cleaned = String(value || "").trim()
    if (cleaned === "" || cleaned === "—") return ""
    return cleaned
  }

  fieldValue(form, fieldName) {
    const field = form.querySelector(`[name$='[${fieldName}]']`)
    return field ? field.value.trim() : ""
  }

  parseAmount(value) {
    const raw = String(value || "").trim()
    if (raw === "" || raw === "—") return null

    const normalized = raw.replace(/[^0-9.-]/g, "")
    const parsed = Number.parseFloat(normalized)

    return Number.isFinite(parsed) ? parsed : null
  }

  normalizeKey(value) {
    return String(value || "").trim().toLowerCase()
  }

  displayAccountName(value) {
    return String(value || "").trim()
  }

  basisTargetKeys(basisCents, accountNames, { includeTaxExempt }) {
    return Array.from(basisCents.keys()).filter((key) => {
      if (key === PRORATED_ACCOUNT_KEY) return false
      if (!includeTaxExempt && this.taxExemptByAccountName(accountNames.get(key) || key)) return false

      return (basisCents.get(key) || 0) > 0
    })
  }

  sourceBucket() {
    return { totalCents: 0, byAccount: new Map() }
  }

  addSourceAmount(source, accountKey, cents) {
    source.totalCents += cents
    if (accountKey) this.addCents(source.byAccount, accountKey, cents)
  }

  addCents(map, key, cents) {
    map.set(key, (map.get(key) || 0) + cents)
  }

  isSalesTaxLine(description) {
    const text = String(description || "")
    if (INCLUDED_TAX_REGEX.test(text)) return false

    return SALES_TAX_REGEX.test(text)
  }

  isFuelSurchargeLine({ description, accountKey }) {
    return accountKey === PRORATED_ACCOUNT_KEY || FUEL_SURCHARGE_REGEX.test(String(description || ""))
  }

  toCents(value) {
    return Math.round(Number(value) * 100)
  }

  fromCents(value) {
    return Number(value) / 100
  }

  allocateSource({
    totalsCents,
    accountNames,
    source,
    targetKeys,
    basisCents,
  }) {
    if (source.totalCents === 0) return

    const basisByKey = new Map()
    targetKeys.forEach((key) => {
      const basis = basisCents.get(key) || 0
      if (basis > 0) basisByKey.set(key, basis)
    })
    if (basisByKey.size === 0) return

    const allocations = this.allocateByBasis(source.totalCents, basisByKey, accountNames)
    if (allocations.length === 0) return

    source.byAccount.forEach((sourceCents, key) => {
      this.addCents(totalsCents, key, -sourceCents)
    })

    allocations.forEach((allocation) => {
      this.addCents(totalsCents, allocation.key, allocation.allocatedCents)
      if (!accountNames.has(allocation.key)) {
        accountNames.set(allocation.key, allocation.name)
      }
    })
  }

  allocateByBasis(totalCents, basisByKey, accountNames) {
    if (totalCents === 0 || basisByKey.size === 0) return []

    const absoluteTotal = Math.abs(totalCents)
    const denominator = Array.from(basisByKey.values()).reduce((sum, value) => sum + value, 0)
    if (denominator <= 0) return []

    const allocations = Array.from(basisByKey.entries()).map(([key, basisCents]) => {
      const numerator = absoluteTotal * basisCents
      return {
        key,
        name: accountNames.get(key) || this.displayAccountName(key),
        basisCents,
        allocatedCents: Math.floor(numerator / denominator),
        remainder: numerator % denominator,
      }
    })

    const allocatedBaseCents = allocations.reduce((sum, allocation) => sum + allocation.allocatedCents, 0)
    let remainingCents = absoluteTotal - allocatedBaseCents
    allocations
      .slice()
      .sort((left, right) => {
        if (left.remainder !== right.remainder) return right.remainder - left.remainder
        return left.name.localeCompare(right.name)
      })
      .forEach((allocation) => {
        if (remainingCents <= 0) return
        allocation.allocatedCents += 1
        remainingCents -= 1
      })

    const sign = totalCents < 0 ? -1 : 1
    allocations.forEach((allocation) => {
      allocation.allocatedCents *= sign
      delete allocation.remainder
    })

    return allocations
  }

  compareRows(left, right) {
    if (left.totalCents !== right.totalCents) return right.totalCents - left.totalCents

    return left.name.localeCompare(right.name)
  }

  pillClass(name) {
    return this.coaPaletteClass("coa-pill", name)
  }

  ensureActiveFilterExists(rows) {
    if (!this.activeFilterKey) return

    const activeExists = rows.some((row) => this.normalizeKey(row.name) === this.activeFilterKey)
    if (!activeExists) this.activeFilterKey = null
  }

  renderPills(rows) {
    this.pillsTarget.replaceChildren()

    const fragment = document.createDocumentFragment()

    rows.forEach((row) => {
      const copyValue = this.copyValue(this.fromCents(row.totalCents))

      const pill = document.createElement("div")
      pill.className = `invoice-total-pill ${this.pillClass(row.name)}`
      pill.dataset.invoiceCoaTotalsKey = row.key

      const top = document.createElement("div")
      top.className = "invoice-total-pill__top"

      const label = document.createElement("span")
      label.className = "invoice-total-pill__label"
      label.textContent = row.name
      label.setAttribute("title", row.name)

      const actions = document.createElement("div")
      actions.className = "invoice-total-pill__actions"

      const copyButton = document.createElement("button")
      copyButton.type = "button"
      copyButton.className = "invoice-total-pill__icon-button invoice-total-pill__copy-button"
      copyButton.dataset.action = "click->invoice-copy#copy"
      copyButton.dataset.invoiceCopyValue = copyValue
      copyButton.setAttribute("aria-label", `Copy ${row.name} amount`)
      copyButton.setAttribute("title", "Copy amount")
      copyButton.innerHTML = COPY_ICON_SVG

      const filterButton = document.createElement("button")
      filterButton.type = "button"
      filterButton.className = "invoice-total-pill__icon-button invoice-total-pill__filter-button"
      filterButton.dataset.action = "click->invoice-coa-totals#toggleFilter"
      filterButton.dataset.invoiceCoaTotalsKey = row.key
      filterButton.setAttribute("aria-pressed", "false")
      filterButton.setAttribute("aria-label", `Filter line items by ${row.name}`)
      filterButton.setAttribute("title", "Filter line items")
      filterButton.innerHTML = FILTER_ICON_SVG

      actions.append(copyButton, filterButton)
      top.append(label, actions)

      const value = document.createElement("span")
      value.className = "invoice-total-pill__value"
      value.textContent = this.formatCurrency(this.fromCents(row.totalCents))

      const subtotal = document.createElement("span")
      subtotal.className = "invoice-total-pill__subtotal"
      subtotal.textContent = `${row.subtotalLabel}: ${this.formatCurrency(this.fromCents(row.subtotalCents))}`

      pill.append(top, value, subtotal)

      // Tax exempt is a company-level setting changed only on the tax settings screen, so this
      // is a read-only badge. renderPills() replaces everything inside the pills target, so it
      // has to reproduce the server-rendered badge or the reviewer loses all indication of which
      // accounts are exempt the moment the first refresh runs.
      if (this.taxExemptByAccountName(row.name)) {
        const footer = document.createElement("div")
        footer.className = "invoice-total-pill__footer"

        const taxBadge = document.createElement("span")
        taxBadge.className = "invoice-total-pill__tax-badge"
        taxBadge.textContent = "Tax exempt"

        footer.append(taxBadge)
        pill.append(footer)
      }

      fragment.appendChild(pill)
    })

    this.pillsTarget.appendChild(fragment)
  }

  applyRowFilter() {
    const activeFilterKey = this.activeFilterKey

    this.rowElements().forEach((row) => {
      const rowKey = this.normalizeKey(this.readAccountName(row))
      row.hidden = Boolean(activeFilterKey && rowKey !== activeFilterKey)
    })

    this.syncPillFilterState()
  }

  syncPillFilterState() {
    const activeFilterKey = this.activeFilterKey

    this.pillsTarget.querySelectorAll(".invoice-total-pill").forEach((pill) => {
      const pillKey = this.normalizeKey(pill.dataset.invoiceCoaTotalsKey)
      const isActive = Boolean(activeFilterKey && pillKey === activeFilterKey)
      pill.classList.toggle("is-filtered", isActive)
      const filterButton = pill.querySelector(".invoice-total-pill__filter-button")
      if (filterButton) {
        filterButton.setAttribute("aria-pressed", isActive ? "true" : "false")
      }
    })
  }

  copyValue(value) {
    return Number(value).toFixed(2)
  }

  formatCurrency(value) {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return "—"

    const sign = numeric < 0 ? "-" : ""
    const absolute = Math.abs(numeric)
    const [digits, cents] = absolute.toFixed(2).split(".")
    const groupedDigits = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",")

    return `${sign}$${groupedDigits}.${cents}`
  }

  taxExemptByAccountName(accountName) {
    const normalized = this.normalizeKey(accountName)
    const taxExemptAccountNames = this.hasTaxExemptAccountNamesValue ? this.taxExemptAccountNamesValue : []
    return taxExemptAccountNames.some((name) => this.normalizeKey(name) === normalized)
  }

  subtotalLabelForAccountName(accountName) {
    return this.taxExemptByAccountName(accountName)
      ? "Subtotal before surcharge"
      : "Subtotal before tax/surcharge"
  }

  coaPaletteClass(prefix, accountName) {
    const key = this.normalizeKey(accountName)
    if (!key) return `${prefix}--default`
    if (key === PRORATED_ACCOUNT_KEY) return `${prefix}--prorated`

    return `${prefix}--${COA_PALETTE_SUFFIXES[this.stablePaletteIndex(key)]}`
  }

  stablePaletteIndex(key) {
    let hash = 0

    for (const char of key) {
      hash = ((hash * 31) + char.charCodeAt(0)) % COA_PALETTE_SUFFIXES.length
    }

    return hash
  }
}
